import React from 'react';

import { App } from '@flipdish/api-client-typescript';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';
import { compose } from 'recompose';

import LazyComponent from '@fd/ui/LazyComponent';

import { toKebabCase } from '../../helpers/strings';
import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { flagService } from '../../services/flagService';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';

const VoucherDetails = lazyWithRetry(() => import('./VoucherDetails'));
const LumpDiscount = lazyWithRetry(() => import('./LumpDiscount'));
const PercentageDiscount = lazyWithRetry(() => import('./PercentageDiscount'));
const MenuItemDiscount = lazyWithRetry(() => import('./MenuItemDiscount'));
const FreeDeliveryFee = lazyWithRetry(() => import('./FreeDeliveryFee'));
const AddNewVoucher = lazyWithRetry(() => import('./AddNewVoucher'));
const VouchersPage = lazyWithRetry(() => import('./VouchersPage/VouchersPage'));

const { AppResourceSetEnum } = App;

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

type Props = RouteComponentProps<{ appId: string }> & MappedProps;

const VoucherRoutes: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    canEdit,
    isFreeDeliveryFeeVoucherEnabled, // TODO - remove when freeDeliveryFeeVoucher is fully released
    isMenuItemVoucherEnabled, // TODO - remove when menuItemVoucher is fully released
    match: { path, params },
    translate,
  } = props;

  const toParentRMS = `/${params?.appId}/vouchers?tab=rms`;
  return (
    <ErrorBoundary identifier="voucher">
      <Switch>
        {canEdit && (
          <Route
            path={`${path}/new`}
            render={(p) => (
              <Switch>
                <Route
                  exact
                  path={`${p.match.path}/lump`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <LumpDiscount {...routeProps} />
                    </LazyComponent>
                  )}
                />
                <Route
                  exact
                  path={`${p.match.path}/percentage`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <PercentageDiscount {...routeProps} />
                    </LazyComponent>
                  )}
                />
                {isMenuItemVoucherEnabled && (
                  <Route
                    exact
                    path={`${p.match.path}/menuItem`}
                    render={(routeProps) => (
                      <LazyComponent>
                        <MenuItemDiscount {...routeProps} />
                      </LazyComponent>
                    )}
                  />
                )}
                {isFreeDeliveryFeeVoucherEnabled && (
                  <Route
                    exact
                    path={`${p.match.path}/freeDelivery`}
                    render={(routeProps) => (
                      <LazyComponent>
                        <FreeDeliveryFee {...routeProps} />
                      </LazyComponent>
                    )}
                  />
                )}
                <Route
                  exact
                  path={`${p.match.path}`}
                  render={() => (
                    <LazyComponent>
                      <AddNewVoucher />
                    </LazyComponent>
                  )}
                />

                <Redirect to={`${p.match.path}`} />
              </Switch>
            )}
          />
        )}

        <Route
          path={`${path}/:voucherId(\\d+)`}
          render={(p) => (
            <Switch>
              {canEdit && (
                <Route
                  exact
                  path={`${p.match.path}/lump`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <LumpDiscount {...routeProps} />
                    </LazyComponent>
                  )}
                />
              )}
              {canEdit && (
                <Route
                  exact
                  path={`${p.match.path}/percentage`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <PercentageDiscount {...routeProps} />
                    </LazyComponent>
                  )}
                />
              )}
              {canEdit && (
                <Route
                  exact
                  path={`${p.match.path}/menuItem`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <MenuItemDiscount {...routeProps} />
                    </LazyComponent>
                  )}
                />
              )}
              {canEdit && (
                <Route
                  exact
                  path={`${p.match.path}/freeDelivery`}
                  render={(routeProps) => (
                    <LazyComponent>
                      <FreeDeliveryFee {...routeProps} />
                    </LazyComponent>
                  )}
                />
              )}
              <Route
                exact
                path={`${p.match.path}`}
                render={(routeProps) => (
                  <LazyComponent>
                    <VoucherDetails {...routeProps} />
                  </LazyComponent>
                )}
              />

              <Redirect to={`${p.match.path}`} />
            </Switch>
          )}
        />

        <Route
          exact
          path={`${path}`}
          render={() => (
            <LazyComponent>
              <VouchersPage />
            </LazyComponent>
          )}
        />

        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.DISCOUNT_OFFERS)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                toParent={toParentRMS}
                subscription={RMSSubscriptions.BASE}
                title={translate('Discount_Offers')}
                module={RMSModules.DISCOUNT_OFFERS}
                url={`modules/marketing/${RMSModules.DISCOUNT_OFFERS}`}
              />
            </LazyComponent>
          )}
        />

        <Route
          exact
          path={`${path}/${toKebabCase(RMSModules.VOUCHER_CODES)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                toParent={toParentRMS}
                subscription={RMSSubscriptions.BASE}
                title={translate('Voucher_Codes')}
                module={RMSModules.VOUCHER_CODES}
                url={`modules/marketing/${RMSModules.VOUCHER_CODES}`}
                includePropertyIdInUrl
                allowFranchisorLevel
              />
            </LazyComponent>
          )}
        />

        <Redirect to={`${path}`} />
      </Switch>
    </ErrorBoundary>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  const { permissions } = state;
  const canView = checkVisibility(permissions, [AppResourceSetEnum.ViewVouchers], []);
  const canEdit = checkVisibility(permissions, [AppResourceSetEnum.EditVouchers], []);

  return {
    canEdit,
    canView,
    isFreeDeliveryFeeVoucherEnabled: flagService.isFlagOn(state, 'freeDeliveryFeeVoucher'), // TODO - remove when freeDeliveryFeeVoucher is fully released
    isMenuItemVoucherEnabled: flagService.isFlagOn(state, 'menuItemVoucher'), // TODO - remove when menuItemVoucher is fully released
    translate: getTranslate(state.locale),
  };
}

export { VoucherRoutes };
export default compose<Props, RouteComponentProps>(connect(mapStateToProps))(VoucherRoutes);
